import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Rocket, Sparkles, Trophy, Gift, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { AnnouncementCard } from "@/components/announcements/AnnouncementCard";
import { Skeleton } from "@/components/ui/skeleton";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useNewFeatureBadge } from "@/hooks/useNewFeatureBadge";
import { Card, CardContent } from "@/components/ui/card";
import { ActivePollCard } from "@/components/polls/ActivePollCard";

const GettingStarted = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { highlightAnnouncements, featuredAnnouncements, regularAnnouncements, isLoading } = useAnnouncements();
  const [banner, setBanner] = useState<{ image_url: string; link_url: string | null } | null>(null);
  const { shouldShowBadge, markAsViewed } = useNewFeatureBadge();

  useEffect(() => {
    markAsViewed("support_chat");
  }, []);

  const displayType = isMobile ? "MOBILE" : "DESKTOP";

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchBanner = async () => {
      const { data } = await supabase
        .from('community_banners')
        .select('image_url, link_url')
        .eq('is_active', true)
        .eq('type', 'GETTING_STARTED')
        .eq('display', displayType)
        .order('created_at', { ascending: false })
        .limit(1);
      if (data && data.length > 0 && data[0].image_url) {
        setBanner(data[0] as { image_url: string; link_url: string | null });
      }
    };
    fetchBanner();
  }, [displayType]);

  const featureAnnouncements = regularAnnouncements.filter(a => a.type === 'feature');
  const tipAnnouncements = regularAnnouncements.filter(a => a.type === 'tip');
  const updateAnnouncements = regularAnnouncements.filter(a => a.type === 'update');

  return (
    <>
      <Helmet>
        <title>Comece Aqui - Data Hire Club</title>
        <meta name="description" content="Bem-vindo ao Data Hire Club! Descubra como aproveitar ao máximo a plataforma, ganhar pontos e desbloquear conquistas." />
      </Helmet>

      <CandidateLayout maxWidth="5xl">
        {/* Banner */}
        {banner && (
          <div className="mb-8">
            <AspectRatio ratio={isMobile ? 1 : 12 / 5}>
              {banner.link_url ? (
                <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                  <img
                    src={banner.image_url}
                    alt="Banner do Comece Aqui"
                    className="rounded-lg w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ) : (
                <img
                  src={banner.image_url}
                  alt="Banner do Comece Aqui"
                  className="rounded-lg w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </AspectRatio>
          </div>
        )}

        {/* Enquete ativa da plataforma */}
        <ActivePollCard />

        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Rocket className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Comece Aqui</h1>
          <p className="text-muted-foreground text-lg">
            Tudo que você precisa saber para aproveitar ao máximo o Data Hire Club
          </p>
        </div>

        {/* Card Chat de Feedback */}
        <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-8 transition-all hover:shadow-md">
          <CardContent className="flex items-start gap-4 p-6">
            <div className="p-3 rounded-full bg-blue-100 shrink-0">
              <MessageCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-lg font-semibold">Chat de Feedback e Guia de Uso</h2>
                {shouldShowBadge("support_chat") && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full animate-pulse">NOVO</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Agora você pode enviar feedbacks, tirar dúvidas sobre funcionalidades e encontrar o que precisa na plataforma! 
                Basta clicar no <strong>botão flutuante no canto inferior direito</strong> da tela.
              </p>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <div className="grid md:grid-cols-2 gap-4">
              <Skeleton className="h-40" />
              <Skeleton className="h-40" />
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Highlights Dinâmicos */}
            {highlightAnnouncements.length > 0 && (
              <section className="space-y-4">
                {highlightAnnouncements.map((announcement) => (
                  <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                  />
                ))}
              </section>
            )}

            {/* Featured Announcements */}
            {featuredAnnouncements.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Destaque</h2>
                </div>
                <div className="space-y-4">
                  {featuredAnnouncements.map((announcement) => (
                    <AnnouncementCard 
                      key={announcement.id} 
                      announcement={announcement} 
                      isFeatured 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Feature Announcements */}
            {featureAnnouncements.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Funcionalidades</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {featureAnnouncements.map((announcement) => (
                    <AnnouncementCard 
                      key={announcement.id} 
                      announcement={announcement} 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Tips */}
            {tipAnnouncements.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Dicas</h2>
                </div>
                <div className="space-y-4">
                  {tipAnnouncements.map((announcement) => (
                    <AnnouncementCard 
                      key={announcement.id} 
                      announcement={announcement} 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Updates */}
            {updateAnnouncements.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Rocket className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Novidades</h2>
                </div>
                <div className="space-y-4">
                  {updateAnnouncements.map((announcement) => (
                    <AnnouncementCard 
                      key={announcement.id} 
                      announcement={announcement} 
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </CandidateLayout>
    </>
  );
};

export default GettingStarted;
